import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, ArrowRight } from 'lucide-react';
import './UpcomingEvents.css';
import { getRegistrationCountForDay } from '../services/industrialVisitService';
import { getDepartmentSettings, clearDepartmentSettingsCache } from '../services/departmentService';
import { getWebDesignRegistrationCount } from '../services/webDesignService';
import { getAllEvents, getSubmissionCountForEvent } from '../services/eventFormService';

import { DestinationCard } from './ui/card-21'; // Import DestinationCard
import technoSapiansImg from '../assets/TechnoSapians_Cover.jpeg';
import orientationImg from '../assets/Orientation_cover.png';
import orbitImg from "../assets/orbit.jpeg"
import recruitmentImg from "../assets/recruitement.jpg"
import workshopImg from "../assets/workshop.jpg"
import educationalImg from "../assets/educational.webp"

const UpcomingEvents = ({ onPageChange }) => {
  // Registration data for each day
  const [registrationCounts, setRegistrationCounts] = useState({});
  const [imcDayRegistrationStatus, setImcDayRegistrationStatus] = useState({
    'Orientation': false,
    'Educational Visit': false,
    'Techo Sapians 2k27': false
  });
  const [eventCapacity, setEventCapacity] = useState(55); // Default/fallback capacity
  const [eventCapacities, setEventCapacities] = useState({}); // Per-event capacities

  // Dynamic events created by admin
  const [dynamicEvents, setDynamicEvents] = useState([]);
  const [dynamicCounts, setDynamicCounts] = useState({});

  // Fetch registration counts for all days
  useEffect(() => {
    const fetchRegistrationCounts = async () => {
      try {
        const quizCount = await getRegistrationCountForDay('Quiz Competition');
        const webDesignCount = await getWebDesignRegistrationCount();
        const technoSapionCount = await getRegistrationCountForDay('Techno Sapiens');

        setRegistrationCounts({
          'Quiz Competition': quizCount,
          'Web Design Competition': webDesignCount,
          'Techno Sapiens': technoSapionCount
        });
      } catch (error) {
        console.error('Error fetching registration counts:', error);
      }
    };

    fetchRegistrationCounts();

    // Poll for updates every 10 seconds
    const intervalId = setInterval(fetchRegistrationCounts, 10000);

    // Listen for registration updates
    const handleRegistrationUpdate = () => {
      fetchRegistrationCounts();
    };

    window.addEventListener('registrationSubmitted', handleRegistrationUpdate);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('registrationSubmitted', handleRegistrationUpdate);
    };
  }, []);

  // Fetch IMC day registration status and capacity from admin settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Clear cache to ensure we get fresh settings
        clearDepartmentSettingsCache();
        const settings = await getDepartmentSettings();
        setImcDayRegistrationStatus(settings.imcDayRegistrationStatus || {
          'Quiz Competition': true,
          'Web Design Competition': true,
          'Techno Sapiens': false
        });
        setEventCapacity(settings.eventCapacity || 55);
        setEventCapacities(settings.eventCapacities || {});
        console.log('Fetched capacities in UpcomingEvents:', settings.eventCapacities || settings.eventCapacity);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();

    // Poll for capacity changes every 10 seconds
    const intervalId = setInterval(fetchSettings, 10000);

    return () => clearInterval(intervalId);
  }, []);

  // Listen for IMC day registration toggle events and capacity changes from admin panel
  useEffect(() => {
    const handleIMCDayRegistrationToggle = (event) => {
      setImcDayRegistrationStatus(prev => ({
        ...prev,
        [event.detail.dayName]: event.detail.isOpen
      }));
    };

    const handleEventSettingsReset = (event) => {
      console.log('Event settings reset detected, updating state...');
      setImcDayRegistrationStatus(event.detail.settings.imcDayRegistrationStatus);
    };

    const handleCapacityChange = async (e) => {
      const { day, capacity } = e.detail || {};
      console.log('Capacity change event received in UpcomingEvents:', day, capacity);
      if (day && typeof capacity === 'number') {
        setEventCapacities(prev => ({ ...prev, [day]: capacity }));
      } else if (typeof capacity === 'number') {
        setEventCapacity(capacity);
      }

      // Also refetch from database to ensure consistency
      try {
        const settings = await getDepartmentSettings();
        setEventCapacity(settings.eventCapacity || 55);
        setEventCapacities(settings.eventCapacities || {});
      } catch (error) {
        console.error('Error verifying capacity:', error);
      }
    };

    window.addEventListener('imcDayRegistrationToggled', handleIMCDayRegistrationToggle);
    window.addEventListener('eventSettingsReset', handleEventSettingsReset);
    window.addEventListener('eventCapacityChanged', handleCapacityChange);

    return () => {
      window.removeEventListener('imcDayRegistrationToggled', handleIMCDayRegistrationToggle);
      window.removeEventListener('eventSettingsReset', handleEventSettingsReset);
      window.removeEventListener('eventCapacityChanged', handleCapacityChange);
    };
  }, []);

  const events = [
    {
      id: 0,
      key: 'Educational Visit',
      title: "Educational Visit",
      date: "2026",
      location: "Offline",
      cohort: "All Branches",
      type: "Coming Soon",
      status: "Get Ready!",
      description: "More details will be announced soon",
      imageUrl: educationalImg,
      flag: "📅",
      themeColor: "340 80% 50%",
      registrationLink: null
    },
    {
      id: 3,
      key: 'Workshop',
      title: "Workshop",
      date: "2026",
      location: "Auditorium",
      cohort: "All Branches",
      type: "Coming Soon!",
      status: "Get Ready!",
      description: "Get ready for something exciting and invaluable",
      imageUrl: workshopImg,
      flag: "🎯",
      themeColor: "340 80% 50%",
      registrationLink: null
    },
    {
      id: 1,
      key: 'Techo Sapians 2k27',
      title: "Techo Sapians 2k27",
      date: "2027",
      location: "Offline",
      cohort: "All Branches",
      type: "Coming Soon",
      status: "Coming Soon",
      description: "More details will be announced soon",
      imageUrl: technoSapiansImg,
      flag: "⚙️",
      themeColor: "260 80% 60%",
      registrationLink: null
    }
  ];

  // Fetch dynamic events from Firestore (admin-created)
  useEffect(() => {
    const fetchDynamicEvents = async () => {
      try {
        const events = await getAllEvents();
        setDynamicEvents(events);

        // Fetch submission counts for each dynamic event
        const counts = {};
        await Promise.all(
          events.map(async (ev) => {
            counts[ev.id] = await getSubmissionCountForEvent(ev.id);
          })
        );
        setDynamicCounts(counts);
      } catch (err) {
        console.error('Failed to fetch dynamic events:', err);
      }
    };
    fetchDynamicEvents();
    const interval = setInterval(fetchDynamicEvents, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      className="upcoming-events"
      id="events"
    >
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title text-3xl md:text-5xl font-bold mb-4 font-['Audiowide'] leading-[1.3]">
            Upcoming <span className="gradient-text ml-2">Events</span>
          </h2>
          <p className="section-subtitle">
            Exciting opportunities await—dive into workshops, fests designed to spark innovation and growth.
          </p>
        </motion.div>

        <motion.div
          className="events-grid"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}
        >
          {/* ─── Dynamic events from admin (Firestore) ─── */}
          {dynamicEvents.map((ev, index) => {
            const filled = dynamicCounts[ev.id] || 0;
            const cap = ev.capacity || 0;
            const percent = cap ? Math.min(100, Math.round((filled / cap) * 100)) : 0;
            const isFull = cap && filled >= cap;
            const isOpen = ev.isRegistrationOpen !== false;

            return (
              <motion.div
                key={ev.id}
                className="event-card group relative overflow-hidden rounded-[24px] border border-white/10 bg-[#0A0A0A] p-6 transition-all duration-300 hover:-translate-y-2 hover:border-[#8B5CF6]/50 hover:shadow-[0_20px_40px_-15px_rgba(139,92,246,0.2)]"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {ev.imageUrl && (
                  <div
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url("${ev.imageUrl}")` }}
                  />
                )}
                <div className="absolute inset-0 z-0 bg-[#0A0A0A]/80 transition-opacity duration-500 group-hover:bg-[#0A0A0A]/60" />

                <div className="relative z-20 mb-6 flex flex-wrap gap-3">
                  <span className="inline-flex items-center rounded-full bg-[#8B5CF6] px-4 py-1.5 text-xs font-bold text-white shadow-[0_0_15px_rgba(139,92,246,0.4)]">
                    {ev.type || 'Event'}
                  </span>
                  {ev.date && (
                    <span className="inline-flex items-center rounded-full bg-[#10B981] px-4 py-1.5 text-xs font-bold text-white">
                      {ev.date}
                    </span>
                  )}
                </div>

                <div className="relative z-20 mb-6">
                  <h3 className="mb-2 text-2xl font-bold text-white">{ev.title}</h3>
                  <p className="text-sm font-medium text-gray-400">{ev.description}</p>
                </div>

                <div className="relative z-20 mb-6 space-y-3">
                  {ev.location && (
                    <div className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-2.5 text-sm text-gray-300 backdrop-blur-sm">
                      <MapPin size={16} className="text-[#6366F1]" />
                      <span>{ev.location}</span>
                    </div>
                  )}
                  {ev.cohort && (
                    <div className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-2.5 text-sm text-gray-300 backdrop-blur-sm">
                      <Users size={16} className="text-[#06B6D4]" />
                      <span>{ev.cohort}</span>
                    </div>
                  )}
                </div>

                <motion.button
                  className={`relative z-20 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white transition-all ${!isOpen ? 'cursor-not-allowed bg-gray-700' :
                    'bg-gradient-to-r from-[#8B5CF6] to-[#6366F1] shadow-[0_4px_20px_rgba(99,102,241,0.4)] hover:shadow-[0_8px_30px_rgba(99,102,241,0.6)]'
                    }`}
                  whileHover={isOpen && !isFull ? { scale: 1.02 } : {}}
                  whileTap={isOpen && !isFull ? { scale: 0.98 } : {}}
                  onClick={() => {
                    if (!isOpen || isFull) return;
                    onPageChange(`event_register_${ev.id}`);
                  }}
                  disabled={!isOpen || isFull}
                >
                  {!isOpen ? 'Coming Soon' : isFull ? 'Registrations Full' : 'Register Now'}
                  {isOpen && !isFull && <ArrowRight size={16} />}
                </motion.button>
              </motion.div>
            );
          })}

          {/* ─── Hardcoded events ─── */}

          {events.map((event, index) => {
            const key = event.key || event.title;
            const filled = registrationCounts[key] || 0;
            const percent = Math.min(100, Math.round((filled / event.cap) * 100));
            const isFull = filled >= event.cap;
            // If event has external registration link, always show as open
            const isRegistrationOpen = event.registrationLink ? true : (imcDayRegistrationStatus[key] === true);

            // Generate stats string
            const stats = `${filled}/${event.cap} Registered • ${event.cohort}`;

            const anchorId = `event-${key.toLowerCase().replace(/\s+/g, '-')}`;

            return (
              <motion.div
                key={event.id}
                id={anchorId}
                className="event-card group relative overflow-hidden rounded-[24px] border border-white/10 bg-[#0A0A0A] p-6 transition-all duration-300 hover:-translate-y-2 hover:border-[#8B5CF6]/50 hover:shadow-[0_20px_40px_-15px_rgba(139,92,246,0.2)]"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {/* Background Image - left blank/black until event artwork is ready */}
                {event.imageUrl && (
                  <div
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url("${event.imageUrl}")` }}
                  />
                )}

                {/* Dark Overlay for text readability */}
                <div className="absolute inset-0 z-0 bg-[#0A0A0A]/80 transition-opacity duration-500 group-hover:bg-[#0A0A0A]/60" />

                {/* Ray/Shine Effect - Revealing "Flash" */}
                <div className="absolute inset-0 z-0 -skew-x-12 overflow-hidden">
                  <div className="absolute -inset-full top-0 block h-full w-1/2 -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 blur-md animate-shine" />
                </div>

                {/* Glow Effect - Added pointer-events-none */}
                <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-b from-white/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                {/* Badges */}
                <div className="relative z-20 mb-6 flex flex-wrap gap-3">
                  <span className="inline-flex items-center rounded-full bg-[#8B5CF6] px-4 py-1.5 text-xs font-bold text-white shadow-[0_0_15px_rgba(139,92,246,0.4)]">
                    {event.type || event.status || 'Event'}
                  </span>
                  {event.date && (
                    <span className="inline-flex items-center rounded-full bg-[#10B981] px-4 py-1.5 text-xs font-bold text-white">
                      {event.date}
                    </span>
                  )}
                </div>

                {/* Content: Title & Desc */}
                <div className="relative z-20 mb-6">
                  <h3 className="mb-2 text-2xl font-bold text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                    {event.title}
                  </h3>
                  <p className="text-sm font-medium text-gray-400">
                    {event.description}
                  </p>
                </div>

                {/* Data Rows */}
                <div className="relative z-20 mb-6 space-y-3">
                  <div className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-2.5 text-sm text-gray-300 backdrop-blur-sm transition-colors hover:bg-white/10">
                    <MapPin size={16} className="text-[#6366F1]" />
                    <span>{event.location}</span>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-2.5 text-sm text-gray-300 backdrop-blur-sm transition-colors hover:bg-white/10">
                    <Users size={16} className="text-[#06B6D4]" />
                    <span>{event.cohort}</span>
                  </div>
                </div>

                {/* Capacity Section */}


                {/* Action Button */}
                <motion.button
                  className={`relative z-20 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white transition-all 
                    ${!isRegistrationOpen
                      ? 'cursor-not-allowed bg-gray-700 hover:bg-gray-600'
                      : isFull
                        ? 'cursor-not-allowed bg-red-600/20 text-red-400 border border-red-500/30'
                        : 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] shadow-[0_4px_20px_rgba(99,102,241,0.4)] hover:shadow-[0_8px_30px_rgba(99,102,241,0.6)]'
                    }`}
                  whileHover={isRegistrationOpen && !isFull ? { scale: 1.02 } : {}}
                  whileTap={isRegistrationOpen && !isFull ? { scale: 0.98 } : {}}
                  onClick={() => {
                    if (!isRegistrationOpen || isFull) return;
                    // Open external registration link if available
                    if (event.registrationLink) {
                      window.open(event.registrationLink, '_blank');
                    } else if (key === "Quiz Competition") onPageChange('quiz_registration');
                    else if (key === "Web Design Competition") onPageChange('web_design');
                    else if (key === "Techno Sapiens") onPageChange('imc_day1');
                  }}
                  disabled={!isRegistrationOpen || isFull}
                >
                  {!isRegistrationOpen ? event.status : isFull ? 'Registrations Full' : 'Apply Now'}
                  {isRegistrationOpen && !isFull && <ArrowRight size={16} />}
                </motion.button>


              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default UpcomingEvents;
